import { UserPlus } from 'lucide-react';
import { LiksDropdown } from '../LiksDropdown/LiksDropdown';
import { TenantUserListPhoto } from '../../../features/user/components/UserListPhoto';
import { formatInitials } from '../../../features/user/userDisplay';
import type { DirectoryUser } from '../../../types/user';
import { MemberEmailCopy } from './MemberEmailCopy';
import { withName } from './membersPanelUtils';
import type {
  LiksMembersPanelLabels,
  LiksMembersPanelRoleOption,
} from './LiksMembersPanel.types';

type LiksMembersInviteRowProps<TRole extends string> = {
  directoryUser: DirectoryUser;
  displayName: string;
  email: string | null;
  inviteRole: TRole;
  roleOptions: ReadonlyArray<LiksMembersPanelRoleOption<TRole>>;
  labels: LiksMembersPanelLabels;
  pendingUserId: string | null;
  onInviteRoleChange: (userId: string, role: TRole) => void;
  onInvite: (userId: string, role: TRole) => void;
};

export function LiksMembersInviteRow<TRole extends string>({
  directoryUser,
  displayName,
  email,
  inviteRole,
  roleOptions,
  labels,
  pendingUserId,
  onInviteRoleChange,
  onInvite,
}: LiksMembersInviteRowProps<TRole>) {
  const userId = directoryUser.id!;

  return (
    <li className="liks-members__list-item">
      <TenantUserListPhoto
        thumbnail={directoryUser.thumbnail}
        initials={formatInitials(displayName)}
        className="liks-members__photo"
      />
      <div className="liks-members__person">
        <span className="liks-members__person-name">
          <span className="liks-members__person-name-text">{displayName}</span>
        </span>
        {email && (
          <MemberEmailCopy
            email={email}
            copyLabel={labels.copyEmail}
            copiedLabel={labels.copyEmailCopied}
          />
        )}
      </div>
      <div className="liks-members__person-actions">
        <LiksDropdown
          className="liks-members__role-dropdown"
          variant="on-light"
          value={inviteRole}
          disabled={pendingUserId === userId}
          options={[...roleOptions]}
          aria-label={withName(labels.inviteRoleLabel, displayName)}
          onChange={(nextValue) =>
            onInviteRoleChange(userId, nextValue as TRole)
          }
        />
        <button
          type="button"
          className="liks-members__action liks-members__action--primary"
          aria-label={withName(labels.addMember, displayName)}
          disabled={pendingUserId === userId}
          onClick={() => onInvite(userId, inviteRole)}
        >
          <UserPlus strokeWidth={2} aria-hidden />
          <span>{labels.add}</span>
        </button>
      </div>
    </li>
  );
}
